import { useEffect, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import {
  subscribeConfirmDialog,
  type ConfirmDialogRequest,
} from "@/lib/confirmDialog";

/**
 * Singleton host that renders confirmation dialogs triggered by
 * {@link confirmDialog}. Mounted once at the root of the app so any code
 * path can await an in-app confirmation without maintaining local state
 * or rendering its own AlertDialog.
 */
export default function ConfirmDialogHost() {
  const [req, setReq] = useState<ConfirmDialogRequest | null>(null);

  useEffect(() => subscribeConfirmDialog(setReq), []);

  const open = req !== null;

  const handleOpenChange = (v: boolean) => {
    if (!v && req) req.resolve(false);
  };

  const isDestructive = req?.variant === "destructive";

  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogContent className="max-w-md max-h-[85vh] flex flex-col">
        <AlertDialogHeader className="shrink-0">
          <AlertDialogTitle>{req?.title}</AlertDialogTitle>
        </AlertDialogHeader>
        {req?.description && (
          <AlertDialogDescription
            asChild
            className="whitespace-pre-line overflow-y-auto flex-1 min-h-0 pr-1"
          >
            <div>{req.description}</div>
          </AlertDialogDescription>
        )}
        <AlertDialogFooter className="shrink-0">
          <AlertDialogCancel onClick={() => req?.resolve(false)}>
            {req?.cancelLabel ?? "Annuler"}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={() => req?.resolve(true)}
            className={cn(
              isDestructive &&
                "bg-destructive text-destructive-foreground hover:bg-destructive/90",
            )}
          >
            {req?.confirmLabel ?? "Confirmer"}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
